<template>
  <div class="flex space-x-2">
    <InputPopinSearch v-model="country" class="bg-charcoal-100 dark:bg-charcoal-800 font-medium flex items-center justify-center px-3 rounded-lg text-sm cursor-pointer" :class="[ isDisabled ? 'pointer-events-none' : '' ]" :options="regions">
      <IconBase :name="`countries/${country}`" class="mr-1.5" /> +{{ getCountryCodeForRegionCode(country) }}
    </InputPopinSearch>

    <InputBase
      :placeholder="placeholder"
      :is-required="isRequired"
      input-mode="tel"
      :is-disabled="isDisabled"
      :model-value="phoneNumber"
      @update:model-value="handleUpdateValue"
      ref="$input"
    />
  </div>
</template>

<script lang="ts" setup>
import { getSupportedRegionCodes, getCountryCodeForRegionCode, getExample, parsePhoneNumber } from 'awesome-phonenumber'

import countriesFR from 'i18n-iso-countries/langs/fr.json'
import countriesEN from 'i18n-iso-countries/langs/en.json'

import { EXCLUDED_COUNTRIES } from '@/static/countries'

const props = withDefaults(defineProps<{
  id?: string
  modelValue: string
  isDisabled?: boolean
  isRequired?: boolean
  label?: string
}>(), {
  id: Math.random().toString(),
  isDisabled: false,
  isRequired: false,
  label: '',
})

const { modelValue } = toRefs(props)
const $input = ref()
const country = ref('FR')
const phoneNumber = ref()

const regions = computed(() => getSupportedRegionCodes().filter(code => !EXCLUDED_COUNTRIES.includes(code)).map(code => {
  const COUNTRIES = useNuxtApp().$i18n.locale.value === 'en' ? countriesEN : countriesFR
  const country = COUNTRIES.countries[code as keyof typeof COUNTRIES.countries]

  return {
    label: country ? `(+${getCountryCodeForRegionCode(code)}) ${Array.isArray(country) ? country[0] : country}` : '',
    labelDisplayed: `+${getCountryCodeForRegionCode(code)}`,
    isPriority: ['FR', 'BE', 'CH', 'ES', 'GB', 'DE', 'US', 'CA'].includes(code),
    icon: `countries/${code}`,
    value: code
  }
}).filter(c => c.label))

watch(country, () => {
  if ($input.value) $input.value.focus()
})

const placeholder = computed(() => country.value ? getExample(country.value, 'mobile')?.number?.national : '')

const handleUpdateValue = (value: string) => {
  const number = parsePhoneNumber(value, { regionCode: country.value })
  emit('update:modelValue',
    number.valid && number.number?.international ? number.number.international : ''
  )
}

watch(
  modelValue,
  (v) => {
    const number = parsePhoneNumber(v, { regionCode: country.value })
    if (number.valid) phoneNumber.value = number.number.national
  },
  { immediate: true }
)
const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

defineExpose({
  focus: () => {
    if ($input.value) $input.value.focus()
  },
  blur: () => {
    if ($input.value) $input.value.blur()
  }
})
</script>